import { game, director, error, Node, UITransform, clamp01, Director, find, Vec3, CCClass, Enum, Component, log } from 'cc';
import { DEBUG } from 'cc/env';
import NumberPrecision from 'number-precision';

/**
 * 根據提供的 frame 數和 fps，計算出時間
 * @param frame 格數
 * @param fps 預設值為動畫素材的 fps
 * @returns 跑完幀數所需時間(in second)
 */
export function calculateFrameTime(frame: number, fps: number = 30): number {
    return frame / fps;
}

/**
 * 將參數填入字串中，參數位置以 {n}表示，ex. 'You won {0} on extra'
 * @param text 目標字串
 * @param args 目標參數
 */
export function fillTextArgument(text: string, args: string[]): string {
    args.forEach((value, idx) => {
        text = text.replace(new RegExp(`\\{${idx}\\}`, 'g'), value);
    });
    text = text.replace(/\{\d+\}/g, '');
    return text;
}

/**
 * 根據傳入的 host 和 args 組成 URL
 */
export function joinURL(host: string, ...args: string[]): string {
    let url: string = host;
    // 判斷是否需要補 '/'
    if (!url.endsWith('/')) {
        url += '/';
    }
    args.forEach((arg, idx) => {
        // remove '/' at the beginning or end of arg
        arg = arg.replace(/^\/|\/$/g, '');
        // 補上參數
        url += `${idx === 0 ? '' : '/'}${arg}`;
    });
    return url;
}

/**
 * 將傳入的 entries 作為 URLSearchParams 合入 path 中
 */
export function joinURLParam(path: string, entries: [string, string][]): string {
    const url = new URL(path);
    entries.forEach(([key, value]) => {
        // 補上參數
        url.searchParams.append(key, value);
    });
    return url.href;
}

export function breakValueToSmallerValues(value: number, smallerValues: Readonly<number[]>): number[] {
    const result: number[] = new Array(smallerValues.length).fill(0);
    const originValue = value;
    for (let i = smallerValues.length - 1; i >= 0; i--) {
        if (!smallerValues[i]) {
            continue;
        }
        const quotient = Math.floor(NumberPrecision.divide(value, smallerValues[i]));
        result[i] = quotient;
        value = NumberPrecision.minus(value, NumberPrecision.times(quotient, smallerValues[i]));
        if (value === 0) {
            break;
        }
    }
    if (DEBUG) {
        // eslint-disable-next-line no-console
        console.assert(
            originValue === result.reduce((pre, cur, idx) => pre + cur * smallerValues[idx], 0),
            `breakValueToSmallerValues failed, originValues: ${originValue}, smallerValues: ${smallerValues}`,
        );
    }
    return result;
}

export function deIdentification(str: string, displayLength: number, replaceWith: string) {
    return str
        .split('')
        .map((char) => {
            if (displayLength > 0) {
                if (char.match(/[\u4E00-\u9FFF]/)) {
                    displayLength -= 2;
                } else {
                    displayLength--;
                }
                return char;
            }
            return replaceWith;
        })
        .join('');
}

export function abbreviateString(str: string, maxLength: number, frontLength = undefined, rearLength = 0, ellipsis = '') {
    if (getLocaleStringLength(str) <= maxLength) {
        return str;
    }
    frontLength = frontLength ?? maxLength;
    const front = substringByLocale(str, 0, frontLength);
    const rear = substringByLocale(str, -rearLength, rearLength);
    return `${front}${ellipsis}${rear}`;
}

export function getLocaleStringLength(str: string): number {
    return str.split('').reduce((pre, cur) => pre + (cur.match(/[\u4E00-\u9FFF]/) ? 2 : 1), 0);
}

export function substringByLocale(str: string, localeStart: number, localeLength: number): string {
    let result = '';
    let currentLength = 0;
    // handle start < 0
    if (localeStart < 0) {
        localeStart = getLocaleStringLength(str) + localeStart;
    }
    for (let i = 0; i < str.length; i++) {
        if (currentLength >= localeStart + localeLength) {
            break;
        }
        if (currentLength >= localeStart) {
            result += str[i];
        }
        currentLength += str[i].match(/[\u4E00-\u9FFF]/) ? 2 : 1;
    }
    return result;
}

export function generateRandomString(length: number) {
    // generate random 36-digit string as dummy user token
    const characterSet: string = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let result: string = '';
    for (let i = 0; i < length; i++) {
        result += characterSet.charAt(Math.trunc(Math.random() * characterSet.length));
    }
    return result;
}

/**
 * 隨機打亂陣列順序 (Fisher-Yates shuffle)
 */
export function shuffleArray<T>(arr: T[]): T[] {
    const result = [...arr]; // 複製原陣列，不破壞原本 array
    for (let i = result.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [result[i], result[j]] = [result[j], result[i]]; // 交換元素
    }
    return result;
}

export async function copyToClipboard(text: string) {
    if (navigator.clipboard && window.isSecureContext) {
        try {
            await navigator.clipboard.writeText(text);
            return true;
        } catch (reason) {
            error('Failed to copy with Clipboard API, Reason:', reason);
            log('Falling back to execCommand method');
        }
    }
    const input = document.createElement('input');
    input.value = text;
    input.style.position = 'absolute';
    input.style.opacity = '0';
    input.style.left = '0px';
    input.style.top = '0px';
    document.body.appendChild(input);
    input.focus();
    input.select();
    try {
        document.execCommand('copy');
        return true;
    } catch (err) {
        error('Failed to copy with execCommand, Reason:', err);
        return false;
    } finally {
        input.remove();
    }
}

/**
 * 設置父節點且不更改位置
 * @param child 子節點
 * @param parent 父節點
 */
export function setParentWithNoPositionChange(child: Node, parent: Node) {
    const originWorldPosition = child.parent.getComponent(UITransform).convertToWorldSpaceAR(child.position);
    child.parent = parent;
    child.position = parent.getComponent(UITransform).convertToNodeSpaceAR(originWorldPosition);
}

/**
 * 設定目標Node下所有Node的Layer
 * @param node 目標Node
 * @param layerTarget 目標Layer
 */
export function setLayerRecursively(node: Node, layerTarget: number) {
    node.layer = layerTarget;
    
    const children = node.children;
    for (let i = 0; i < children.length; i++) {
        setLayerRecursively(children[i], layerTarget);
    }
}

export function weightedRandom(weights: number[]): number {
    const totalWeight = weights.reduce((acc, weight) => acc + weight, 0);
    const random = Math.random() * totalWeight;
    let cumulativeWeight = 0;
    for (let i = 0; i < weights.length; i++) {
        cumulativeWeight += weights[i];
        if (random < cumulativeWeight) {
            return i;
        }
    }
    return weights.length - 1;
}

export function captureCanvasAsBase64(withTransparent: boolean = false) {
    return new Promise<string>((resolve) => {
        director.once('director_after_draw', () => {
            resolve(game.canvas.toDataURL(withTransparent ? 'image/png' : 'image/jpeg'));
        });
    });
}

/**
 * @param mean average (default: 0)
 * @param stdev standard deviation (default: 1)
 * @param normalized whether to normalize the result to [0, 1] (default: true)
 * @returns a normal distributed random number
 * @link https://stackoverflow.com/questions/25582882/javascript-math-random-normal-distribution-gaussian-bell-curve
 */
export function gaussianRandom(mean = 0, stdev = 1, normalized = true) {
    const u = 1 - Math.random(); // Converting [0,1) to (0,1]
    const v = Math.random();
    const z = Math.sqrt(-2.0 * Math.log(u)) * Math.cos(2.0 * Math.PI * v);
    // Transform to the desired mean and standard deviation:
    const result = z * stdev + mean;

    if (!normalized) {
        return result;
    }

    // Normalize to [0, 1]
    const normalizedResult = (result + 3 * stdev) / (6 * stdev);
    // Clamp the result to [0, 1] to handle rare cases where it might go out of bounds due to numerical precision issues
    return clamp01(normalizedResult);
}

export function combination(n: number, r: number): number {
    if (r > n || n <= 0 || r < 0) {
        return 0;
    }

    if (r === 0 || r === n) {
        return 1;
    }

    return (combination(n - 1, r - 1) * n) / r;
}

// response [base64 string, file name]
export async function loadLocalFile(readAsText: boolean = false) {
    return new Promise<[string, string]>((resolve) => {
        const input = document.createElement('input');
        input.type = 'file';
        input.onchange = (event: any) => {
            const { target }: { target: { files: { name: string }[] } } = event;
            const reader = new FileReader();
            reader.onload = () => {
                resolve([reader.result as string, target.files[0].name]);
            };
            if (readAsText) {
                reader.readAsText(input.files[0]);
            } else {
                reader.readAsDataURL(input.files[0]);
            }
        };
        input.click();
    });
}
// response [base64 string, file name][]
export async function loadMultipleLocalFile(readAsText: boolean = false) {
    return new Promise<[string, string][]>((resolve) => {
        const input = document.createElement('input');
        input.type = 'file';
        input.multiple = true;
        input.onchange = (event: any) => {
            const { target }: { target: { files: { name: string }[] } } = event;
            const result = [...target.files].map((file, i) => {
                const reader = new FileReader();
                return new Promise<[string, string]>((readerResolve) => {
                    reader.onload = () => {
                        readerResolve([reader.result as string, file.name]);
                    };
                    if (readAsText) {
                        reader.readAsText(input.files[i]);
                    } else {
                        reader.readAsDataURL(input.files[i]);
                    }
                });
            });
            Promise.all(result).then(resolve);
        };
        input.click();
    });
}

export function exportJsonFile(data: Object | string, fileName: string) {
    const dataStr = typeof data === 'string' ? data : JSON.stringify(data);
    const blob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${fileName}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
}

export function takeAScreenshot() {
    return new Promise<string>((resolve) => {
        director.once(Director.EVENT_AFTER_DRAW, () => {
            resolve(game.canvas.toDataURL('image/png'));
        });
    });
}

export function convertCenterPointToWorldSpace(position: Vec3) {
    const canvasTransform = find('Canvas').transform;
    return canvasTransform.convertToWorldSpaceAR(position);
}

export function convertWorldPointToCenterSpace(worldPosition: Vec3) {
    const canvasTransform = find('Canvas').transform;
    return canvasTransform.convertToNodeSpaceAR(worldPosition);
}

const defaultLightColor = 'blueviolet';
const defaultDarkColor = 'yellow';
export function chooseColorByTheme(
    { light = defaultLightColor, dark = defaultDarkColor }: { light?: string; dark?: string } = { light: defaultLightColor, dark: defaultDarkColor },
) {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? dark : light;
}

export function setEnumAttr(obj, propName, enumDef) {
    CCClass.Attr.setClassAttr(obj, propName, 'type', 'Enum');
    CCClass.Attr.setClassAttr(obj, propName, 'enumList', Enum.getList(enumDef));
}

/**
 * 比較兩個列舉物件是否擁有相同的 [key, value] 組合（僅比較自身可列舉、值為整數的成員）。
 * TS enum 的反向映射（例如 `'0': '<None>'`）值是字串，會被排除，因此一個 TS 預設 enum
 * 與由它複製出來的 `Enum({})` 副本會被視為相同。`null`/`undefined` 視為沒有任何成員。
 * @param a 列舉物件（或 null/undefined）
 * @param b 列舉物件（或 null/undefined）
 * @returns 兩者的整數值成員是否完全相同
 */
export function hasSameEnumEntries(a: object | null | undefined, b: object | null | undefined): boolean {
    const entriesOf = (obj: object | null | undefined): [string, number][] =>
        obj ? (Object.entries(obj).filter(([, value]) => Number.isInteger(value)) as [string, number][]) : [];
    const entriesA = entriesOf(a);
    const entriesB = entriesOf(b);
    if (entriesA.length !== entriesB.length) {
        return false;
    }
    const mapB = new Map(entriesB);
    return entriesA.every(([key, value]) => mapB.get(key) === value);
}

/**
 * 下拉選單「未設定」項目的標籤。Creator 會把列舉名稱當成 HTML 渲染，
 * 直接寫 `<None>` 會顯示成空白選項，因此使用跳脫形式（`EnhanceAnimation.getClipsEnum` 也是這個寫法）。
 */
export const ENUM_NONE_LABEL = '&lt;None&gt;';

/**
 * 回傳在最前面加上 {@link ENUM_NONE_LABEL} = 0 的新列舉；原列舉每個整數值成員 `name → v` 變成 `name → v + 1`。
 * 篩選條件與 {@link hasSameEnumEntries} 相同（TS enum 的字串反向鍵會被忽略）。
 * @param enumDef 原列舉（或 null/undefined）
 * @returns 新列舉；`enumDef` 為 null/undefined 時回傳 null
 */
export function withNoneOption(enumDef: object | null | undefined): any {
    if (!enumDef) {
        return null;
    }
    const shifted: Record<string, number> = { [ENUM_NONE_LABEL]: 0 };
    for (const [name, value] of Object.entries(enumDef)) {
        if (Number.isInteger(value)) {
            shifted[name] = (value as number) + 1;
        }
    }
    return Enum(shifted);
}

export function getArithmeticSequence(min: number, max: number, step: number = 1): number[] {
    return Array.from({ length: Math.floor((max - min) / step) + 1 }, (_, idx) => min + NumberPrecision.times(idx, step));
}

const component = new Component();

/**
 * @param action action to be executed per frame
 * @param timesPerFrame execute the action for how many times per frame, default is 1
 * @param totalTimes total times to execute the action, default is timesPerFrame
 * @param dynamic whether to dynamically adjust the execution times based on FPS, default is false
 * @returns a promise that resolves when execution is finished and a function to stop the execution
 */
export function executeActionPerFrame<T>(action: () => T, timesPerFrame?: number, totalTimes?: number, dynamic?: boolean): [Promise<T[]>, () => void] {
    // check if input is valid
    if (!action) {
        throw Error('executeActionPerFrame: action is required');
    }
    timesPerFrame = timesPerFrame ?? 1;
    totalTimes = totalTimes ?? timesPerFrame;
    const baseTimesPerFrame = timesPerFrame;
    // ensure timesPerFrame is not greater than totalTimes
    timesPerFrame = Math.min(timesPerFrame, totalTimes);
    if (timesPerFrame <= 0 || totalTimes <= 0) {
        throw Error('executeActionPerFrame: timesPerFrame and totalTimes should both be greater than 0');
    }

    let stop = false;
    const promise = new Promise<T[]>((resolve) => {
        let currentTimes = 0;
        const results: T[] = [];
        const execute = () => {
            const { deltaTime } = game;
            const minFPS = 10;
            const currentFPS = deltaTime > 0 ? 1 / deltaTime : 60;
            let dynamicTimesPerFrame = baseTimesPerFrame;
            if (dynamic) {
                if (currentFPS < minFPS) {
                    dynamicTimesPerFrame = Math.max(1, Math.floor(baseTimesPerFrame / 2));
                } else if (currentFPS > minFPS) {
                    dynamicTimesPerFrame = baseTimesPerFrame * 2;
                }
            }
            const targetExecuteTimes = Math.min(dynamicTimesPerFrame, totalTimes - currentTimes);
            for (let i = 0; i < targetExecuteTimes; i++) {
                results.push(action());
                currentTimes++;
            }
            if (currentTimes >= totalTimes || stop) {
                component.unschedule(execute);
                resolve(results);
            }
        };
        component.schedule(execute, 0);
        execute();
    });

    return [
        promise,
        () => {
            stop = true;
        },
    ];
}

/**
 * Creates a debounced function that delays invoking the provided function until after a specified wait time has elapsed
 * @param func function to be debounced
 * @param wait wait time in milliseconds
 * @param leading whether to invoke the function on the leading edge of the wait interval
 * @returns a debounced version of the provided function
 */
export function debounce<T extends (...args: any[]) => void>(func: T, wait: number, leading: boolean): T {
    let timeout: number = -1;
    return function callback(...args: Parameters<T>) {
        const context = this;
        const callNow = leading && timeout === -1;
        // clear previous timeout
        if (timeout !== -1) {
            clearTimeout(timeout);
        }
        timeout = setTimeout(() => {
            timeout = -1;
            if (!leading) {
                func.apply(context, args);
            }
        }, wait);
        if (callNow) {
            func.apply(context, args);
        }
    } as T;
}

/**
 * Creates a throttled function that only invokes the provided function at most once per specified wait time
 * @param func function to be throttled
 * @param wait wait time in milliseconds
 * @returns a throttled version of the provided function
 */
export function throttle<T extends (...args: any[]) => any>(func: T, wait: number): (force?: boolean, ...args: Parameters<T>) => ReturnType<T> {
    let lastTime = 0;
    return function callback(force?: boolean, ...args: Parameters<T>) {
        const now = Date.now();
        const context = this;
        if (now - lastTime >= wait || force) {
            lastTime = now;
            return func.apply(context, args);
        }
        return null;
    } as T;
}

const EXTNAME_RE = /(\.[^./?\\]*)(\?.*)?$/;

/**
 * @en Get the ext name of a path including '.', like '.png'.
 * @zh 返回 Path 的扩展名，包括 '.'，例如 '.png'。
 * @example {@link cocos/core/utils/CCPath/extname.js}
 */
export function extname(path: string) {
    const temp = EXTNAME_RE.exec(path);
    return temp ? temp[1] : '';
}
